/**
 * UnifiedSaveService.ts
 * Service unifié pour la sauvegarde (fusion de SaveService et CloudManager.saveProject)
 * Utilise SaveQueue pour éviter les race conditions
 * Gère localStorage + Supabase de manière unifiée
 */
import { saveQueue } from './SaveQueue';
import { useAppStore } from './StateService';
import { logger } from '../utils/ProfessionalLogger';
import { ErrorNotificationService } from './ErrorNotificationService';
import {
  type ProjectData,
  CanvasDataSchema,
  validateCanvasData,
  validateProjectData,
} from '../types/ProjectData';
import type { z } from 'zod';
import { supabase, isSupabaseConfigured } from './SupabaseService';

export interface SaveOptions {
  projectId?: string | null;
  saveToCloud?: boolean;
  saveToLocal?: boolean;
  priority?: 'high' | 'normal' | 'low';
  /** A03 : forcer l'écrasement malgré un conflit (choix « Écraser quand même »). */
  force?: boolean;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface SaveResult {
  success: boolean;
  local?: boolean;
  cloud?: boolean;
  /** Vrai si une version plus récente a été détectée en base et écrasée (B2). */
  conflicted?: boolean;
  /** A03 : conflit détecté et sauvegarde cloud BLOQUÉE (en attente de décision). */
  blocked?: boolean;
  error?: Error;
}

export interface LoadResult {
  success: boolean;
  data?: ProjectData;
  error?: Error;
}

/**
 * Structure des données de sauvegarde
 */
export interface SaveDataStructure {
  id: string;
  name: string;
  data: {
    shapes: ReturnType<typeof useAppStore.getState>['shapes'];
    canvas: ReturnType<typeof useAppStore.getState>['canvas'];
    project: ReturnType<typeof useAppStore.getState>['project'];
    selection: ReturnType<typeof useAppStore.getState>['selection'];
  };
  timestamp: number;
  version: string;
  createdAt?: number;
  updatedAt?: number;
}

/**
 * Service unifié de sauvegarde
 * Fusionne SaveService et CloudManager.saveProject()
 */
export class UnifiedSaveService {
  private static instance: UnifiedSaveService | null = null;
  private lastLocalSave: string = '';
  private lastCloudSave: number = 0;
  // B2 : `updated_at` de la dernière version que NOUS avons écrite, par projet.
  // Sert à détecter qu'une autre session a modifié le projet entre-temps.
  private lastKnownUpdatedAt: Map<string, string> = new Map();
  private conflictOnLastCloudSave: boolean = false;
  // A03 : vrai quand la dernière sauvegarde cloud a été BLOQUÉE par un conflit
  // (on n'a PAS écrasé la version distante — décision laissée à l'utilisateur).
  private cloudBlockedByConflict: boolean = false;
  private readonly MIN_CLOUD_SAVE_INTERVAL = 5000; // 5 secondes minimum entre sauvegardes cloud

  private constructor() {}

  public static getInstance(): UnifiedSaveService {
    if (!UnifiedSaveService.instance) {
      UnifiedSaveService.instance = new UnifiedSaveService();
    }
    return UnifiedSaveService.instance;
  }

  /**
   * Sauvegarde le projet actuel
   */
  public async save(options: SaveOptions = {}): Promise<SaveResult> {
    const {
      projectId = null,
      saveToCloud = true,
      saveToLocal = true,
      priority = 'normal',
      force = false,
      onSuccess,
      onError,
    } = options;

    return saveQueue.enqueue(async () => {
      const result: SaveResult = {
        success: false,
      };

      try {
        // Récupérer les données depuis StateService
        const store = useAppStore.getState();
        const data = this.buildSaveData(store, projectId);

        // Validation Zod des données
        const canvasData = {
          shapes: data.data.shapes,
          canvas: data.data.canvas,
          project: data.data.project,
          selection: data.data.selection,
        };

        try {
          validateCanvasData(canvasData);
        } catch (validationError) {
          throw new Error(
            `Données invalides: ${validationError instanceof Error ? validationError.message : String(validationError)}`
          );
        }

        // Vérifier si les données ont changé
        const serialized = JSON.stringify(data);
        if (serialized === this.lastLocalSave && !saveToCloud) {
          result.success = true;
          return result;
        }

        // 1. Sauvegarder localement si demandé
        if (saveToLocal) {
          const localSuccess = await this.saveToLocalStorage(data);
          result.local = localSuccess;
          if (localSuccess) {
            this.lastLocalSave = serialized;
            store.markAsSaved();
          }
        }

        // 2. Sauvegarder dans le cloud si demandé et disponible
        if (saveToCloud && projectId) {
          const now = Date.now();
          if (now - this.lastCloudSave >= this.MIN_CLOUD_SAVE_INTERVAL) {
            const cloudSuccess = await this.saveToSupabase(projectId, data, force);
            result.cloud = cloudSuccess;
            result.conflicted = this.conflictOnLastCloudSave;
            result.blocked = this.cloudBlockedByConflict;
            if (cloudSuccess) {
              this.lastCloudSave = now;
            }
          } else {
            result.cloud = true; // On considère comme réussi car on a déjà sauvegardé récemment
          }
        }

        // A03 : un conflit BLOQUÉ n'est PAS une erreur — la sauvegarde locale a
        // réussi, seule la synchro cloud est différée jusqu'à décision utilisateur.
        result.success =
          result.local !== false && (result.cloud !== false || result.blocked === true);

        if (result.success) {
          onSuccess?.();
        } else {
          const error = new Error('Échec de la sauvegarde');
          onError?.(error);
          throw error;
        }

        return result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        result.error = err;
        result.success = false;

        ErrorNotificationService.getInstance().notifyError(err, {
          severity: 'high',
          category: 'system',
          context: { action: 'save', projectId },
          retryable: true,
          retryAction: async () => {
            await this.save(options);
          },
        });

        onError?.(err);
        throw err;
      }
    }, priority);
  }

  /**
   * Charge un projet
   */
  public async load(projectId: string): Promise<LoadResult> {
    try {
      // Essayer de charger depuis Supabase d'abord
      if (isSupabaseConfigured() && supabase) {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (user) {
            const { data, error } = await supabase
              .from('template_editor_projects')
              .select('*')
              .eq('id', projectId)
              .eq('user_id', user.id)
              .single();

            if (!error && data) {
              const projectData: ProjectData = {
                id: data.id,
                name: data.name,
                data: data.canvas_data as z.infer<typeof CanvasDataSchema>,
                createdAt: new Date(data.created_at).getTime(),
                updatedAt: new Date(data.updated_at).getTime(),
              };

              // Validation : validateProjectData attend un JSON string,
              // on lui passe la version sérialisée.
              validateProjectData(JSON.stringify(projectData));

              return {
                success: true,
                data: projectData,
              };
            }
          }
        } catch (supabaseError) {
          logger.warn(
            'Erreur lors du chargement depuis Supabase, fallback sur localStorage:',
            supabaseError
          );
        }
      }

      // Fallback sur localStorage
      const STORAGE_KEY = 'snapbooth_autosave';
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const data = JSON.parse(stored) as SaveDataStructure;
          if (data.id === projectId) {
            const projectData: ProjectData = {
              id: data.id,
              name: data.name,
              data: data.data as z.infer<typeof CanvasDataSchema>,
              createdAt: data.createdAt ? new Date(data.createdAt).getTime() : Date.now(),
              updatedAt: data.updatedAt ? new Date(data.updatedAt).getTime() : Date.now(),
            };

            validateProjectData(JSON.stringify(projectData));

            return {
              success: true,
              data: projectData,
            };
          }
        } catch (parseError) {
          logger.error('Erreur lors du parsing des données localStorage:', parseError);
        }
      }

      return {
        success: false,
        error: new Error('Projet non trouvé'),
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return {
        success: false,
        error: err,
      };
    }
  }

  /**
   * Construit les données de sauvegarde depuis le store
   */
  private buildSaveData(
    store: ReturnType<typeof useAppStore.getState>,
    projectId: string | null
  ): SaveDataStructure {
    return {
      id: projectId || store.project.id || this.generateProjectId(),
      name: store.canvas.documentName || store.project.name || 'projet-snapbooth',
      // P3 : structure simplifiée (CanvasState complet n'est pas requis ici, on serialise).

      data: {
        shapes: store.shapes,
        canvas: {
          templateWidth: store.canvas.templateWidth,
          templateHeight: store.canvas.templateHeight,
          backgroundColor: store.canvas.backgroundColor,
          documentName: store.canvas.documentName,
        },
        project: store.project,
        selection: store.selection,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
      createdAt: store.project.createdAt?.getTime() || Date.now(),
      updatedAt: Date.now(),
      // P3 : `timestamp` requis par SaveDataStructure (snapshot temporel).
      timestamp: Date.now(),
      version: '1.0.0',
    };
  }

  /**
   * Sauvegarde dans localStorage
   */
  private async saveToLocalStorage(data: SaveDataStructure): Promise<boolean> {
    try {
      const STORAGE_KEY = 'snapbooth_autosave';
      const BACKUP_KEY = 'snapbooth_backup';

      const serialized = JSON.stringify(data);

      const sizeInMB = serialized.length / (1024 * 1024);
      if (sizeInMB > 4) {
        logger.warn(`Données volumineuses: ${sizeInMB.toFixed(2)}MB`);
        const compressed = {
          ...data,
          data: {
            ...data.data,
            shapes: data.data.shapes.slice(0, 100),
          },
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(compressed));
      } else {
        localStorage.setItem(STORAGE_KEY, serialized);
      }

      try {
        localStorage.setItem(BACKUP_KEY, serialized);
      } catch (backupError) {
        logger.warn('Impossible de créer le backup:', backupError);
      }

      return true;
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde localStorage:', error);

      if (
        error instanceof Error &&
        (error.name === 'QuotaExceededError' || error.message.includes('quota'))
      ) {
        this.clearOldStorage();
        try {
          const serialized = JSON.stringify(data);
          localStorage.setItem('snapbooth_autosave', serialized);
          return true;
        } catch (retryError) {
          logger.error('Impossible de sauvegarder même après nettoyage:', retryError);
          return false;
        }
      }

      return false;
    }
  }

  /**
   * Sauvegarde dans Supabase (cloud)
   * P0.2 FIX: Crée le projet s'il n'existe pas, sinon le met à jour
   */
  private async saveToSupabase(
    projectId: string,
    data: SaveDataStructure,
    force: boolean = false
  ): Promise<boolean> {
    try {
      this.cloudBlockedByConflict = false;
      if (!isSupabaseConfigured() || !supabase) {
        logger.warn('Supabase non configuré, skip sauvegarde cloud');
        return false;
      }

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        logger.warn('Utilisateur non authentifié, skip sauvegarde cloud');
        return false;
      }

      const canvasData = {
        shapes: data.data.shapes,
        canvas: data.data.canvas,
        project: data.data.project,
        selection: data.data.selection,
      };

      // Validation avant sauvegarde
      validateCanvasData(canvasData);

      // A03 — Détection de conflit (optimistic concurrency léger, fail-safe) :
      // si la version en base diffère de celle que NOUS avons écrite en dernier,
      // c'est qu'une autre session a modifié le projet entre-temps.
      // - Sans `force` : on BLOQUE l'écrasement (return) et on laisse l'utilisateur
      //   décider (modal Recharger / Écraser / Annuler).
      // - Avec `force` : l'utilisateur a explicitement choisi d'écraser → on passe.
      this.conflictOnLastCloudSave = false;
      try {
        const { data: existing } = await supabase
          .from('template_editor_projects')
          .select('updated_at')
          .eq('id', projectId)
          .eq('user_id', user.id)
          .maybeSingle();
        const known = this.lastKnownUpdatedAt.get(projectId);
        if (existing?.updated_at && known && existing.updated_at !== known) {
          this.conflictOnLastCloudSave = true;
          if (!force) {
            // Conflit non résolu → on N'ÉCRASE PAS. La sauvegarde locale (déjà
            // faite) protège le travail ; l'utilisateur tranchera via la modal.
            this.cloudBlockedByConflict = true;
            logger.warn(
              `⛔ Conflit BLOQUÉ sur ${projectId} : version base ${existing.updated_at} ≠ notre base ${known}. Sauvegarde cloud différée.`
            );
            return false;
          }
          logger.warn(`⚠️ Conflit sur ${projectId} ÉCRASÉ sur décision utilisateur (force).`);
        }
      } catch {
        /* En cas de doute (lecture impossible) on n'empêche jamais la sauvegarde. */
      }

      // Génération du thumbnail : aperçu miniature du canvas (150 px de large,
      // JPEG 60 %) stocké dans thumbnail_url. Fail-safe : on n'empêche pas la
      // sauvegarde si Konva n'est pas disponible (édition hors-éditeur, SSR…).
      let thumbnailDataUrl: string | undefined;
      try {
        const { canvasStageRefService } = await import('../hooks/useCanvasStageRef');
        const stage = canvasStageRefService.getStage();
        if (stage) {
          // pixelRatio ≈ 150px / templateWidth → thumbnail toujours ≈ 150px large
          const templateW = data.data?.canvas?.templateWidth ?? 800;
          const ratio = Math.max(0.05, Math.min(0.3, 150 / templateW));
          thumbnailDataUrl = stage.toDataURL({
            pixelRatio: ratio,
            mimeType: 'image/jpeg',
            quality: 0.6,
          });
        }
      } catch {
        /* Pas de thumbnail si le stage n'est pas monté — non bloquant. */
      }

      // P0.2 FIX: Utiliser upsert pour créer ou mettre à jour le projet.
      // NB : un trigger BDD force `updated_at = now()`, donc la valeur qu'on
      // envoie est ignorée — on relit la vraie valeur juste après (cf. ci-dessous)
      // pour fixer la base de référence au MÊME format que les futurs SELECT.
      const { data: upserted, error: upsertError } = await supabase
        .from('template_editor_projects')
        .upsert(
          {
            id: projectId,
            user_id: user.id,
            name: data.name,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            canvas_data: canvasData as any,
            project_type: 'template_editor',
            updated_at: new Date().toISOString(),
            ...(thumbnailDataUrl ? { thumbnail_url: thumbnailDataUrl } : {}),
          },
          { onConflict: 'id' }
        )
        .select('updated_at')
        .maybeSingle();

      if (upsertError) {
        logger.error('Erreur lors de la sauvegarde (upsert) dans Supabase:', upsertError);
        return false;
      }

      // Base de référence = valeur RÉELLE en base (post-trigger), même format que
      // les SELECT de détection → pas de faux conflit.
      if (upserted?.updated_at) {
        this.lastKnownUpdatedAt.set(projectId, upserted.updated_at);
      }
      logger.info(`✅ Projet sauvegardé avec succès (upsert): ${projectId}`);
      return true;
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde Supabase:', error);
      return false;
    }
  }

  /**
   * Génère un ID de projet unique
   */
  private generateProjectId(): string {
    return `project-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Nettoie le localStorage ancien
   */
  private clearOldStorage(): void {
    try {
      const keys = Object.keys(localStorage);
      const oldKeys = keys.filter(
        key =>
          key.startsWith('snapbooth_') && key !== 'snapbooth_autosave' && key !== 'snapbooth_backup'
      );
      oldKeys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      logger.warn('Erreur lors du nettoyage du localStorage:', error);
    }
  }
}
